import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormFeedback,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import BreadCrumb from '../Components/Common/BreadCrumb';
import TableContainer from '../Components/Common/TableContainer';
import DeleteModal from '../Components/Common/DeleteModal';
import { BlogCategoryContext } from '../context/BlogCategoryContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const BlogCategories = () => {
  document.title = 'دسته‌بندی‌های بلاگ | فات 90';

  const { categories, loading, error, fetchCategories, addCategory, editCategory, removeCategory } =
    useContext(BlogCategoryContext);
  const [category, setCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // اعتبارسنجی فرم
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: category?.name || '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('لطفاً نام دسته‌بندی را وارد کنید')
        .max(100, 'نام دسته‌بندی باید حداکثر 100 کاراکتر باشد'),
    }),
    onSubmit: async (values) => {
      try {
        const categoryDto = {
          name: values.name,
        };
        if (isEdit) {
          await editCategory(category.id, categoryDto);
        } else {
          await addCategory(categoryDto);
        }
        toggleModal();
        validation.resetForm();
      } catch (err) {
        // خطاها توسط BlogCategoryContext مدیریت می‌شن
      }
    },
  });

  // مدیریت مودال
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setCategory(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  // افزودن دسته‌بندی جدید
  const handleAddClick = () => {
    setIsEdit(false);
    setCategory(null);
    toggleModal();
  };

  // ویرایش دسته‌بندی
  const handleEditClick = (categoryData) => {
    setIsEdit(true);
    setCategory(categoryData);
    toggleModal();
  };

  // حذف دسته‌بندی
  const onClickDelete = (categoryData) => {
    setCategory(categoryData);
    setDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    if (category) {
      try {
        await removeCategory(category.id);
        setDeleteModal(false);
      } catch (err) {
        // خطاها توسط BlogCategoryContext مدیریت می‌شن
      }
    }
  };

  // دریافت دسته‌بندی‌ها در بارگذاری اولیه
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: 'شناسه',
        accessorKey: 'id',
        enableColumnFilter: false,
      },
      {
        header: 'نام دسته‌بندی',
        accessorKey: 'name',
        enableColumnFilter: false,
      },
      {
        header: 'تعداد پست‌ها',
        accessorKey: 'postCount',
        enableColumnFilter: false,
      },
      {
        header: 'عملیات',
        cell: (cell) => (
          <UncontrolledDropdown>
            <DropdownToggle href="#" className="btn btn-soft-secondary btn-sm" tag="button">
              <i className="ri-more-fill" />
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem
                href="#"
                onClick={() => {
                  const categoryData = cell.row.original;
                  handleEditClick(categoryData);
                }}
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> ویرایش
              </DropdownItem>
              <DropdownItem
                href="#"
                onClick={() => {
                  const categoryData = cell.row.original;
                  onClickDelete(categoryData);
                }}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> حذف
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteCategory}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {isEdit ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی'}
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="name-input" className="form-label">
                نام دسته‌بندی
              </Label>
              <Input
                type="text"
                id="name-input"
                name="name"
                value={validation.values.name}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.name && !!validation.errors.name}
              />
              <FormFeedback>{validation.errors.name}</FormFeedback>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary" disabled={loading}>
              {isEdit ? 'به‌روزرسانی' : 'ثبت'}
            </Button>
            <Button type="button" color="secondary" onClick={toggleModal}>
              لغو
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Container fluid>
        <BreadCrumb title="دسته‌بندی‌های بلاگ" pageTitle="مدیریت" />
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="card-title mb-0">لیست دسته‌بندی‌های بلاگ</h5>
                  </Col>
                  <Col className="text-end">
                    <Button color="primary" onClick={handleAddClick}>
                      <i className="ri-add-line align-bottom me-1"></i> افزودن دسته‌بندی
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                {loading ? (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">در حال بارگذاری...</h5>
                  </div>
                ) : error ? (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">{error}</h5>
                  </div>
                ) : categories && categories.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={categories}
                    isGlobalFilter={true}
                    isAddUserList={false}
                    customPageSize={10}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="جستجو بر اساس نام دسته‌بندی..."
                  />
                ) : (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">هیچ دسته‌بندی یافت نشد</h5>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogCategories;